import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useProjects } from "@/hooks/use-projects";
import { useEmployees } from "@/hooks/use-employees";
import { useOfferTemplates } from "@/hooks/use-offers";
import { useEquipmentList } from "@/hooks/use-equipment";
import { matchesSearch } from "@/lib/equipment/filter";
import { useLocationsOverview } from "@/hooks/use-locations";
import { locationSubtitle, matchesLocationSearch } from "@/lib/locations/filter";
import { useMenuPermissions } from "@/hooks/use-menu-permissions";
import { useIsLeadBooker, withLeadBooker } from "@/hooks/use-lead-booker";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { DBProject } from "@/lib/db-types";
import type { EmployeeRow } from "@/hooks/use-employees";
import type { MenuKey } from "@/lib/menu-registry";
import {
  canAccessMenuKey,
  newOfferPaletteHref,
  paletteNavItems,
} from "@/lib/command-palette-nav";
import { SrIcon } from "@/components/ui/SrIcon";
import { useReturnFocus } from "@/hooks/use-return-focus";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Åpnes via global keydown uten DialogTrigger; Esc skal ikke etterlate fokus på body (VIB-2536).
  useReturnFocus(open);
  const navigate = useNavigate();
  const { activeUser } = useImpersonation();
  const {
    data: menuPerms,
    isLoading: menuLoading,
    isError: menuError,
  } = useMenuPermissions();
  const { data: projects = [] } = useProjects();
  const { data: employees = [] } = useEmployees();
  const { data: templates = [] } = useOfferTemplates();
  const equipmentEnabled = useFeatureFlag("feature_equipment");
  const locationsEnabled = useFeatureFlag("feature_locations");
  const freelancerBank = useFeatureFlag("feature_freelancer_bank");
  const chatRoom = useFeatureFlag("feature_chat_rom");

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((isOpen) => !isOpen);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const isLeadBooker = useIsLeadBooker();
  function canAccess(key: MenuKey): boolean {
    // VIB-2925: frilanserbanken krever i tillegg flagget.
    if (key === "frilansere" && !freelancerBank) return false;
    // VIB-3022: Chat-rommet krever i tillegg flagget.
    if (key === "chat" && !chatRoom) return false;
    return withLeadBooker(
      key,
      canAccessMenuKey(menuPerms, activeUser?.role, key, { loading: menuLoading, error: menuError }),
      isLeadBooker,
    );
  }

  const navItems = useMemo(
    () => paletteNavItems(canAccess),
    // canAccess is derived from the same deps listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuPerms, menuLoading, menuError, activeUser?.role, freelancerBank, chatRoom, isLeadBooker],
  );

  const sizeTemplates = templates.filter((t) => t.kind === "size");
  const q = search.toLowerCase();
  const canTilbud = canAccess("tilbud");
  const canProsjekter = canAccess("prosjekter");
  const canMineOppgaver = canAccess("mine-oppgaver");
  const canAnsatte = canAccess("ansatte");
  // VIB-2917: registeret hentes først når paletten er åpen og noen søker.
  const canUtstyr = equipmentEnabled && canAccess("utstyr");
  const { data: equipment = [] } = useEquipmentList(false, open && canUtstyr && search.length > 0);
  // VIB-2938: lokasjonsbanken, samme mønster som utstyr.
  const canLokasjoner = locationsEnabled && canAccess("lokasjoner");
  const { data: locations = [] } = useLocationsOverview(open && canLokasjoner && search.length > 0);

  const actions = [];
  if (canTilbud) {
    actions.push({
      id: "action-new-offer",
      label: "Nytt tilbud",
      description: "Opprett tilbud",
      icon: "plus" as const,
      href: newOfferPaletteHref(),
      searchText: "nytt tilbud",
    });
    for (const t of sizeTemplates) {
      actions.push({
        id: `action-new-offer-${t.code}`,
        label: `Nytt tilbud: ${t.name}`,
        description: t.description ?? "Forhåndsfyll med mal",
        icon: "file" as const,
        href: newOfferPaletteHref(t.code),
        searchText: `nytt tilbud ${t.name} ${t.code} ${t.description ?? ""}`,
      });
    }
  }
  if (canProsjekter) {
    actions.push({
      id: "action-new-project",
      label: "Nytt prosjekt",
      description: "Opprett prosjekt",
      icon: "folder" as const,
      href: "/prosjekter/ny",
      searchText: "nytt prosjekt",
    });
  }
  if (canMineOppgaver) {
    actions.push({
      id: "action-new-entry",
      label: "Ny registrering",
      description: "Registrer timer",
      icon: "clock" as const,
      href: "/mine-oppgaver?ny=1",
      searchText: "ny registrering timer",
    });
  }

  const visibleActions =
    search.length === 0
      ? actions
      : actions.filter(
          (a) =>
            a.label.toLowerCase().includes(q) ||
            a.description.toLowerCase().includes(q) ||
            a.searchText.toLowerCase().includes(q),
        );

  const pageResults =
    search.length === 0
      ? navItems
      : navItems.filter(
          (page) =>
            page.label.toLowerCase().includes(q) ||
            page.description.toLowerCase().includes(q) ||
            page.searchText.toLowerCase().includes(q),
        );

  const projectResults =
    search.length > 0 && canProsjekter
      ? projects.filter(
          (p) =>
            p.name?.toLowerCase().includes(q) ||
            p.project_code?.toLowerCase().includes(q),
        )
      : [];

  const employeeResults =
    search.length > 0 && canAnsatte
      ? employees.filter((e: EmployeeRow) =>
          e.full_name?.toLowerCase().includes(q),
        )
      : [];

  const equipmentResults =
    search.length > 0 && canUtstyr ? equipment.filter((e) => matchesSearch(e, search)).slice(0, 20) : [];

  const locationResults =
    search.length > 0 && canLokasjoner
      ? locations.filter((l) => l.archived_at === null && matchesLocationSearch(l, search)).slice(0, 20)
      : [];

  function go(path: string) {
    navigate(path);
    setOpen(false);
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Søk sider, handlinger, prosjekter…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>Ingen resultater funnet.</CommandEmpty>

        {visibleActions.length > 0 && (
          <CommandGroup heading="Handlinger">
            {visibleActions.map((action) => (
              <CommandItem
                key={action.id}
                onSelect={() => go(action.href)}
                value={action.searchText}
              >
                <SrIcon name={action.icon} className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{action.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {action.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {pageResults.length > 0 && (
          <CommandGroup heading={search.length === 0 ? "Navigasjon" : "Sider"}>
            {pageResults.map((page) => (
              <CommandItem
                key={page.id}
                onSelect={() => go(page.path)}
                value={page.searchText}
              >
                <SrIcon name={page.icon} className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{page.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {page.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {projectResults.length > 0 && (
          <CommandGroup heading="Prosjekter">
            {projectResults.map((project: DBProject) => (
              <CommandItem
                key={`project-${project.id}`}
                onSelect={() => go(`/prosjekter/${project.id}`)}
                value={`${project.name} ${project.project_code ?? ""} project-${project.id}`}
              >
                <SrIcon name="folder" className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{project.name}</span>
                  {project.project_code && (
                    <span className="text-xs text-muted-foreground">
                      {project.project_code}
                    </span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {employeeResults.length > 0 && (
          <CommandGroup heading="Ansatte">
            {employeeResults.map((employee: EmployeeRow) => (
              <CommandItem
                key={`employee-${employee.id}`}
                onSelect={() => go(`/ansatte/${employee.id}`)}
                value={`${employee.full_name} ${employee.email ?? ""} employee-${employee.id}`}
              >
                <SrIcon name="users" className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{employee.full_name}</span>
                  {employee.email && (
                    <span className="text-xs text-muted-foreground">
                      {employee.email}
                    </span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {equipmentResults.length > 0 && (
          <CommandGroup heading="Utstyr">
            {equipmentResults.map((item) => (
              <CommandItem
                key={`equipment-${item.id}`}
                onSelect={() => go(`/utstyr/${item.id}`)}
                value={`${item.name} ${item.serial_numbers.join(" ")} equipment-${item.id}`}
              >
                <SrIcon name="package" className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{item.name}</span>
                  {item.serial_numbers.length > 0 && (
                    <span className="text-xs text-muted-foreground">{item.serial_numbers.join(", ")}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {locationResults.length > 0 && (
          <CommandGroup heading="Lokasjoner">
            {locationResults.map((l) => (
              <CommandItem
                key={`location-${l.id}`}
                onSelect={() => go(`/lokasjoner/${l.slug}`)}
                value={`${l.name} ${l.address ?? ""} ${l.municipality_name ?? ""} ${l.tags.join(" ")} location-${l.id}`}
              >
                <SrIcon name="map-pin" className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{l.name}</span>
                  <span className="text-xs text-muted-foreground">{locationSubtitle(l)}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
